import React, { useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useDispatch } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import toMoment from '~/lib/toMoment';
import { flashMessage } from '~/app/actions';
import { S, A, DARK } from '~/app/ui/tokens';
import { tr } from '~/app/ui/lang';
import Button from '~/app/ui/Button';
import { Page, PageHeader, Toolbar, Spacer } from '~/app/ui/Page';
import SearchField from '~/app/ui/SearchField';
import Segmented from '~/app/ui/Segmented';
import StatusBadge from '~/app/ui/StatusBadge';
import EmptyState from '~/app/ui/EmptyState';
import Pager from '~/app/ui/Pager';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import { RowMenu } from '~/app/ui/Menu';
import { Table, SkeletonRows, rowClick, useRowClasses } from '~/app/ui/Table';

// Same name as before: editing a person refetches 'getUsers'.
const USERS = gql`
  query getUsers {
    users {
      _id
      username
      email
      createdBy
      createdAt
      updatedBy
      updatedAt
      isSuperAdmin
      isActive
      roles {
        categories {
          category
          roleType
        }
        others {
          category
          roleType
        }
      }
    }
    categories {
      _id
      path
    }
  }
`;
const UPDATE_USER = gql`
  mutation updateUser($id: String!, $user: UserInput) {
    updateUser(_id: $id, user: $user) {
      isActive
    }
  }
`;

type RoleRef = { category: string; roleType?: string | null };
type Person = {
  _id: string;
  username: string;
  email?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
  isSuperAdmin?: boolean;
  isActive?: boolean;
  roles?: { categories?: RoleRef[]; others?: RoleRef[] } | null;
};

const PAGE_SIZE = 25;
const OTHER_LABELS: Record<string, string> = { HEADER: 'Header', FOOTER: 'Footer', BASIC: 'Basic editing', AIXC_DEPLOY: 'AIXC deploy' };

const useStyles = createUseStyles({
  name: { fontWeight: 600 },
  email: { fontSize: 13, color: S.muted, marginTop: 2 },
  access: { fontSize: 13, lineHeight: 1.5, color: '#3A3D44', [DARK]: { color: S.fg } },
  accessRole: { fontWeight: 600, color: S.fg },
  path: { fontFamily: S.mono, fontSize: 12 },
  colStatus: { width: 130 },
  colUpdated: { width: 170, '@media (max-width: 900px)': { display: 'none' } },
  colMenu: { width: 52, textAlign: 'right' },
  when: { whiteSpace: 'nowrap' },
  who: { fontSize: 12, color: S.muted, marginTop: 2 },
  hidden: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' },
  nameLink: { color: S.fg, textDecoration: 'none', '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 }, '&:focus-visible': A.focus }
});

/** "Editor: /us, /cn · Approver: /us · Header" from a person's roles. */
const accessOf = (p: Person, paths: Map<string, string>) => {
  if (p.isSuperAdmin) return [{ role: 'Super admin', where: 'everything' }];
  const byRole = new Map<string, string[]>();
  (p.roles?.categories || []).forEach((r) => {
    const role = r.roleType === 'APPROVER' ? 'Approver' : 'Editor';
    byRole.set(role, [...(byRole.get(role) || []), (paths.get(String(r.category)) || 'unknown category').replace(/\/cxc10/g, '/aixc')]);
  });
  const others = (p.roles?.others || []).map(r => OTHER_LABELS[r.category] || r.category);
  const parts = Array.from(byRole, ([role, where]) => ({ role, where: where.join(', ') }));
  if (others.length) parts.push({ role: 'Also', where: others.join(', ') });
  return parts;
};

const PeopleScreen: React.FC = () => {
  const c = useStyles();
  const rowCls = useRowClasses();
  const history = useHistory();
  const dispatch = useDispatch();
  const { data, loading, error, refetch } = useQuery(USERS, { fetchPolicy: 'cache-and-network' });
  const [updateUser] = useMutation(UPDATE_USER, { refetchQueries: ['getUsers', 'getUser'], awaitRefetchQueries: true });
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'blocked'>('all');
  const [page, setPage] = useState(1);
  const [blocking, setBlocking] = useState<Person | null>(null);

  const people: Person[] = useMemo(() => data?.users || [], [data]);
  const paths = useMemo(() => new Map<string, string>((data?.categories || []).map((cat: { _id: string; path: string }) => [String(cat._id), cat.path])), [data]);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return people
      .filter(p => filter === 'all' || (filter === 'active' ? p.isActive : !p.isActive))
      .filter(p => !needle || [p.username, p.email].some(x => String(x || '').toLowerCase().includes(needle)))
      .sort((a, b) => a.username.localeCompare(b.username));
  }, [people, q, filter]);
  // The list can shrink under the current page (blocking someone with the Active filter on): stay on the last page.
  const lastPage = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const currentPage = Math.min(page, lastPage);
  const visible = shown.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const setActive = async (p: Person, isActive: boolean) => {
    const r = await updateUser({ variables: { id: p._id, user: { _id: p._id, isActive } } });
    if (!r.data?.updateUser || r.data.updateUser.isActive !== isActive) throw new Error('That didn’t go through. Only super admins can change people.');
    dispatch(flashMessage(isActive ? `Unblocked ${p.username}.` : `Blocked ${p.username}. They can’t sign in to Genesis until you unblock them.`));
  };

  let body: React.ReactNode;
  if (error && !data) body = <tr><td colSpan={5}><EmptyState tone="error" title="Couldn’t load people" action={<Button icon="refresh" onClick={() => refetch()}>Try again</Button>}>{error.message}</EmptyState></td></tr>;
  else if (loading && !data) body = <SkeletonRows rows={6} widths={['50%', '70%', 70, 100, 0]} />;
  else if (shown.length === 0) {
    body = <tr><td colSpan={5}><EmptyState icon="group" title={q ? `No one matches “${q}”` : 'No one here'} action={q ? <Button onClick={() => setQ('')}>Clear search</Button> : undefined} /></td></tr>;
  } else {
    body = visible.map((p) => {
      const updated = toMoment(p.updatedAt);
      return (
        <tr key={p._id} className={rowCls.clickable} {...rowClick(() => history.push(`/admin/role/${p._id}`))} data-person={p.username}>
          <td>
            <Link className={`${c.name} ${c.nameLink}`} to={`/admin/role/${p._id}`}>{p.username}</Link>
            {p.email && <div className={c.email}>{p.email}</div>}
          </td>
          <td className={c.access}>
            {accessOf(p, paths).map(part => (
              <div key={part.role}><span className={c.accessRole}>{part.role}</span>{part.where ? <> · <span className={part.role === 'Also' || part.role === 'Super admin' ? undefined : c.path}>{part.where}</span></> : null}</div>
            ))}
            {!p.isSuperAdmin && !(p.roles?.categories || []).length && !(p.roles?.others || []).length && <span style={{ color: S.muted }}>No roles yet</span>}
          </td>
          <td className={c.colStatus}>{p.isActive ? <StatusBadge tone="green" label="Active" /> : <StatusBadge tone="gray" label="Blocked" />}</td>
          <td className={c.colUpdated}>
            {updated ? <div className={c.when} title={updated.format('MMM D, YYYY h:mm A')}>{updated.format('MMM D, YYYY')}</div> : '—'}
            {p.updatedBy && <div className={c.who}>by {p.updatedBy}</div>}
          </td>
          <td className={c.colMenu}>
            <RowMenu
              label={`Actions for ${p.username}`}
              testId={`person-menu-${p.username}`}
              groups={[
                [{ key: 'edit', label: 'Edit roles', icon: 'manage_accounts', to: `/admin/role/${p._id}` }],
                p.isSuperAdmin ? [] : p.isActive
                  ? [{ key: 'block', label: 'Block…', icon: 'block', danger: true, onSelect: () => setBlocking(p) }]
                  : [{ key: 'unblock', label: 'Unblock', icon: 'lock_open', onSelect: () => { setActive(p, true).catch(e => dispatch(flashMessage((e as Error).message, 'ERROR'))); } }]
              ]}
            />
          </td>
        </tr>
      );
    });
  }

  const counts = { all: people.length, active: people.filter(p => p.isActive).length, blocked: people.filter(p => !p.isActive).length };

  return (
    <Page testId="people-screen">
      <PageHeader
        title={tr('People & roles', '用户权限')}
        description="Who can sign in to Genesis and what they can do. Editors and approvers get their rights per category."
        actions={<Button variant="primary" icon="person_add" to="/admin/role/add" data-testid="add-person">Add person</Button>}
      />
      <Toolbar>
        <Segmented
          label="Show"
          value={filter}
          onChange={(v) => { setFilter(v); setPage(1); }}
          options={[
            { value: 'all', label: 'Everyone', count: data ? counts.all : null },
            { value: 'active', label: 'Active', count: data ? counts.active : null },
            { value: 'blocked', label: 'Blocked', count: data ? counts.blocked : null }
          ]}
        />
        <SearchField value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search names and email" testId="people-search" />
        <Spacer />
      </Toolbar>
      <Table label="People" testId="people-table">
        <thead>
          <tr>
            <th>Person</th>
            <th>Access</th>
            <th className={c.colStatus}>Status</th>
            <th className={c.colUpdated}>Updated</th>
            <th className={c.colMenu}><span className={c.hidden}>Actions</span></th>
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </Table>
      {shown.length > PAGE_SIZE && <Pager page={currentPage} pageSize={PAGE_SIZE} total={shown.length} onPage={setPage} noun="people" />}
      {blocking && (
        <ConfirmDialog
          danger
          title={`Block ${blocking.username}?`}
          confirmLabel="Block"
          onConfirm={() => setActive(blocking, false)}
          onClose={() => setBlocking(null)}
          testId="block-dialog"
        >
          <p>They can’t sign in to Genesis until you unblock them. Their roles are kept.</p>
        </ConfirmDialog>
      )}
    </Page>
  );
};

export default PeopleScreen;
